/**
 * Staff List Dialog
 * Shows staff members scheduled for a group on a specific date.
 * Styled consistently with the Capacity Overview children list.
 */

import { useMemo } from 'react';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/macro';
import { formatDateLong } from '@/lib/format';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useShifts } from '@/hooks/useShifts';
import { useAllEmployees } from '@/hooks/useEmployees';
import { getInitials, cn } from '@/lib/utils';
import { useImageUrl } from '@/hooks/useImage';
import { isImageId, isDirectImageUrl } from '@/utils/imageUtils';

interface StaffListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: string;
  date: string;
  /** Called when clicking a staff member — navigate to shift planning */
  onStaffClick?: (employeeId: string) => void;
}

interface StaffMember {
  id: string;
  firstname: string;
  lastname: string;
  avatarUrl?: string;
  shiftTimes: string[];
  qualificationLevel?: string;
  languages?: string[];
}

function StaffListItem({
  staff,
  onClick,
}: {
  staff: StaffMember;
  onClick?: () => void;
}) {
  const { i18n } = useLingui();
  const fullName = `${staff.firstname} ${staff.lastname}`.trim();
  const initials = getInitials(fullName) || '??';

  // Resolve avatar URL
  const avatarUrlRaw = staff.avatarUrl?.trim() || undefined;
  const needsImageResolution = isImageId(avatarUrlRaw);
  const resolvedImageUrl = useImageUrl(needsImageResolution ? avatarUrlRaw : null);

  const finalAvatarUrl = useMemo(() => {
    if (needsImageResolution) return resolvedImageUrl || undefined;
    if (isDirectImageUrl(avatarUrlRaw)) return avatarUrlRaw;
    return undefined;
  }, [needsImageResolution, resolvedImageUrl, avatarUrlRaw]);

  const Wrapper = onClick ? 'button' : 'div';

  return (
    <Wrapper
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 p-2 rounded-lg text-left border border-border',
        onClick && 'hover:bg-accent transition-colors cursor-pointer'
      )}
    >
      <Avatar className="h-10 w-10 flex-shrink-0">
        {finalAvatarUrl && <AvatarImage src={finalAvatarUrl} alt={fullName} />}
        <AvatarFallback>{initials}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
          <span className="font-medium text-sm">{fullName}</span>
          {staff.qualificationLevel === 'QUALIFIED' && (
            <Badge variant="secondary" className="text-xs px-1.5 py-0">
              {i18n._({ id: 'employees.qualification.qualified', message: 'Qualified' })}
            </Badge>
          )}
          {staff.languages?.map((lang) => (
            <Badge key={lang} variant="outline" className="text-xs px-1.5 py-0 uppercase">
              {lang}
            </Badge>
          ))}
        </div>
        <div className="text-xs text-muted-foreground">
          {staff.shiftTimes.join(', ')}
        </div>
      </div>
    </Wrapper>
  );
}

export function StaffListDialog({
  open,
  onOpenChange,
  groupId,
  date,
  onStaffClick,
}: StaffListDialogProps) {
  const { i18n } = useLingui();

  const formattedDate = useMemo(() => {
    return formatDateLong(date, i18n.locale);
  }, [date, i18n.locale]);

  // Fetch shifts for this group on this date
  const { data: shiftsData, isLoading: isLoadingShifts } = useShifts({
    groupId,
    fromDate: date,
    toDate: date,
    limit: 200,
  });

  // Fetch employees for avatar/name data
  const { data: employeesData } = useAllEmployees();

  // Build employee lookup
  const employeeMap = useMemo(() => {
    const map = new Map<
      string,
      {
        firstname?: string;
        lastname?: string;
        avatarUrl?: string;
        qualificationLevel?: string;
        languages?: string[];
      }
    >();
    for (const emp of employeesData || []) {
      if (emp.id) {
        map.set(emp.id, {
          firstname: emp.firstname,
          lastname: emp.lastname,
          avatarUrl: emp.avatarUrl,
          qualificationLevel: emp.qualificationLevel,
          languages: emp.languages
            ?.map((l) => l.language)
            .filter((l): l is string => !!l),
        });
      }
    }
    return map;
  }, [employeesData]);

  // Deduplicate staff and collect shift times
  const staffMembers = useMemo<StaffMember[]>(() => {
    const shifts = shiftsData?.shifts || [];
    const staffMap = new Map<string, StaffMember>();

    for (const shift of shifts) {
      if (!shift.employee_id) continue;
      // Only include shifts that are not cancelled
      if (shift.status === 'CANCELLED') continue;

      const empId = shift.employee_id;
      const existing = staffMap.get(empId);
      const timeStr = `${shift.start_time || '?'} – ${shift.end_time || '?'}`;

      if (existing) {
        existing.shiftTimes.push(timeStr);
      } else {
        const empData = employeeMap.get(empId);
        const shiftEmp = shift.employee;
        staffMap.set(empId, {
          id: empId,
          firstname: empData?.firstname || shiftEmp?.firstname || '',
          lastname: empData?.lastname || shiftEmp?.lastname || '',
          avatarUrl: empData?.avatarUrl,
          qualificationLevel: empData?.qualificationLevel,
          languages: empData?.languages,
          shiftTimes: [timeStr],
        });
      }
    }

    // Sort by name
    return Array.from(staffMap.values()).sort((a, b) =>
      `${a.lastname} ${a.firstname}`.localeCompare(`${b.lastname} ${b.firstname}`)
    );
  }, [shiftsData, employeeMap]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Staff for {formattedDate}</Trans>
          </DialogTitle>
          <DialogDescription className="sr-only">
            <Trans id="shifts.staffList.description">Employees on shift on this day.</Trans>
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 space-y-1.5">
          {/* Loading State */}
          {isLoadingShifts && (
            <div className="space-y-3 py-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 p-2">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!isLoadingShifts && staffMembers.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              <Trans>No staff scheduled for this group on this date</Trans>
            </p>
          )}

          {/* Staff List */}
          {!isLoadingShifts &&
            staffMembers.map((staff) => (
              <StaffListItem
                key={staff.id}
                staff={staff}
                onClick={
                  onStaffClick
                    ? () => {
                        onOpenChange(false);
                        onStaffClick(staff.id);
                      }
                    : undefined
                }
              />
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
